/**
 * Modulo M6: attestati PDF e report Excel.
 * - L'attestato nasce alla consegna del test superato (o su richiesta dell'admin), con un codice
 *   di verifica pubblico; il PDF sta su S3 e si scarica con URL firmato.
 * - Il report del webinar nasce alla chiusura della finestra del test e va all'organizzatore;
 *   è rigenerabile.
 */
import { z } from 'zod';

const id = z.string().uuid();
const dataIso = z.string().datetime({ offset: true });

/** Codice di verifica: 12 caratteri in base32 senza caratteri ambigui, a gruppi di 4. */
export const ALFABETO_CODICE = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const codiceVerificaSchema = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))
  .pipe(z.string().length(12, 'Il codice ha 12 caratteri.').regex(new RegExp(`^[${ALFABETO_CODICE}]+$`), 'Il codice contiene caratteri non validi.'));
export function formattaCodice(codice: string): string {
  return codice.replace(/(.{4})(?=.)/g, '$1-');
}

export const STATI_DOCUMENTO = ['in_coda', 'pronto', 'errore'] as const;
export type StatoDocumento = (typeof STATI_DOCUMENTO)[number];

// ---------------------------------------------------------------- Attestati
export const attestatoMioSchema = z.object({
  id,
  webinarId: id,
  webinarTitolo: z.string(),
  dataWebinar: dataIso,
  codice: z.string(),
  stato: z.enum(STATI_DOCUMENTO),
  emessoAt: dataIso.nullable(),
  revocatoAt: dataIso.nullable(),
  minutiTotali: z.number().int(),
  percentuale: z.number(),
  /** URL firmato di download, presente solo se `pronto` e non revocato */
  url: z.string().url().nullable(),
});
export type AttestatoMio = z.infer<typeof attestatoMioSchema>;

/** Verifica pubblica: mai il codice fiscale. */
export const verificaAttestatoSchema = z.object({
  codice: z.string(),
  valido: z.boolean(),
  motivoNonValido: z.enum(['revocato', 'non_trovato']).nullable(),
  nome: z.string().nullable(),
  cognome: z.string().nullable(),
  webinarTitolo: z.string().nullable(),
  percorsoTitolo: z.string().nullable(),
  dataWebinar: dataIso.nullable(),
  emessoAt: dataIso.nullable(),
  minutiTotali: z.number().int().nullable(),
  esitoTest: z.enum(['superato']).nullable(),
});
export type VerificaAttestato = z.infer<typeof verificaAttestatoSchema>;

export const attestatoAdminSchema = z.object({
  id,
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  codice: z.string(),
  stato: z.enum(STATI_DOCUMENTO),
  errore: z.string().nullable(),
  emessoAt: dataIso.nullable(),
  revocatoAt: dataIso.nullable(),
  revocaMotivo: z.string().nullable(),
  emailInviataAt: dataIso.nullable(),
  url: z.string().url().nullable(),
});
export type AttestatoAdmin = z.infer<typeof attestatoAdminSchema>;
export const revocaAttestatoSchema = z.object({ motivo: z.string().trim().min(3, 'Scrivi il motivo della revoca.').max(500) });
export const riepilogoAttestatiSchema = z.object({ totale: z.number().int(), pronti: z.number().int(), inCoda: z.number().int(), errori: z.number().int(), revocati: z.number().int() });

// ---------------------------------------------------------------- Report
export const TIPI_REPORT = ['webinar'] as const;
export const reportSchema = z.object({
  id,
  tipo: z.enum(TIPI_REPORT),
  stato: z.enum(STATI_DOCUMENTO),
  errore: z.string().nullable(),
  nomeFile: z.string().nullable(),
  generatoAt: dataIso.nullable(),
  inviatoA: z.array(z.string()),
  inviatoAt: dataIso.nullable(),
  url: z.string().url().nullable(),
  richiestoDa: z.string().nullable(),
});
export type Report = z.infer<typeof reportSchema>;
